import { useMemo } from 'react';

interface Props {
  /** Sorted ASC. */
  dates: string[];
  /** Currently selected date. Must be in `dates`. */
  value: string;
  onChange: (date: string) => void;
}

/**
 * HTML range slider over a discrete list of acquisition dates.
 *
 * Design notes:
 *   - 44px min-height thumb target (mobile). Native <input type="range"> on
 *     iOS/Safari already honors accent-color and renders a fat thumb with
 *     touch.action; the wrapper keeps the hit area ≥ 44px regardless.
 *   - Prev/Next buttons so desktop users can key through one acquisition at
 *     a time without having to hit the exact slider pixel.
 *   - Tick marks beneath the track use <datalist> so the browser snaps the
 *     slider onto actual dates — no in-between invalid positions.
 */
export function TimeScrubber({ dates, value, onChange }: Props) {
  const index = Math.max(0, dates.indexOf(value));
  const datalistId = useMemo(() => `scrubber-ticks-${Math.floor(dates.length * 7)}`, [dates.length]);

  function select(idx: number) {
    const clamped = Math.max(0, Math.min(dates.length - 1, idx));
    const next = dates[clamped];
    if (next && next !== value) onChange(next);
  }

  return (
    <div className="flex items-center gap-3 w-full">
      <button
        type="button"
        onClick={() => select(index - 1)}
        disabled={index <= 0}
        aria-label="Previous acquisition"
        className="min-w-[44px] min-h-[44px] px-3 rounded-md bg-bg-card border border-border text-fg-secondary disabled:opacity-40 hover:bg-bg-hover"
      >
        ‹
      </button>

      <div className="flex-1 min-h-[44px] flex flex-col justify-center">
        <input
          type="range"
          min={0}
          max={Math.max(0, dates.length - 1)}
          step={1}
          value={index}
          list={datalistId}
          onChange={(e) => select(Number(e.target.value))}
          className="w-full accent-greeniq-700 cursor-pointer"
          aria-label={`Acquisition date, currently ${value}`}
        />
        <datalist id={datalistId}>
          {dates.map((d, i) => (
            <option key={d} value={i} label={d} />
          ))}
        </datalist>
        <div className="mt-1 text-center text-xs text-fg-muted">
          <span className="font-semibold text-fg">{value}</span>
          <span className="ml-2 text-fg-muted">
            ({index + 1} / {dates.length})
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => select(index + 1)}
        disabled={index >= dates.length - 1}
        aria-label="Next acquisition"
        className="min-w-[44px] min-h-[44px] px-3 rounded-md bg-bg-card border border-border text-fg-secondary disabled:opacity-40 hover:bg-bg-hover"
      >
        ›
      </button>
    </div>
  );
}
